import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
// import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import SiteLocationRichText from '../SiteLocationRichText/SiteLocationRichText';
import {
  TextField,
  RichTextField,
  ImageField,
  Image,
  useSitecoreContext,
  Text,
  // NextImage,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useDispatch } from 'react-redux';
import { siteSelection } from '../../features/siteSelectionSlice';
import { useState } from 'react';
import {
  StCardText,
  StCardTitleText,
  StudyNameHeadingWrapper,
} from '../../components/SitesSelection/SiteSelection.styles';
// import { StNextImage } from 'components/shared/NextImage.styles';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from './PhoneIcon';
import ButtonPrimary from '../../components/shared/Button/PrimaryButton';
import UiBox from '../../components/shared/Box';
import UiHeading from '../../components/shared/Heading';
import TextLinkIcon from './TextLinkIcon';
import { StIframe } from '../../components/shared/UIIframe/UIIframe';
import SiteInfoModal from './SiteInfoModal';
import { isValidUrl, checkUrlExists } from './helper';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import { UIButton } from '../../components/shared/UIButton.styles';
import SiteNameRichText from '../SiteNameRichText/SiteNameRichText';
import { useEffect } from 'react';

export interface ISiteInfo {
  siteCode?: string | number;
  siteName?: string;
  siteAddress?: string;
  siteCity?: string;
  siteState?: string | null;
  siteCountry?: string;
  siteZipCode?: string;
  distance?: string | number;
  isSelected?: string;
  contactPhoneNumber?: string;
  latitude: number;
  longitude: number;
  [key: string]: any; // Index signature for additional properties
  isHybridSite: boolean;
}
interface ISiteInfoCardProps {
  siteInfo: ISiteInfo;
  buttonText: TextField;
  siteAddressFormat: RichTextField;
  siteNameFormat: RichTextField;
  callCenterDefaultNumber?: string;
  sitePhoneNumber: RichTextField;
  isShowSelectedColor?: boolean;
  cardButtonAriaLabel?: string;
  phoneIcon?: ImageField;
  mapIcon?: ImageField;
  virtualIcon?: ImageField;
  virtualMapIcon?: ImageField;
  virutalSiteText?: TextField;
  isCompactCard?: boolean;
  premiereSiteIcon?: ImageField;
  modalButtonText: TextField;
}

const SiteInfoCard: React.FC<any> = (props: ISiteInfoCardProps) => {
  const isShowSelectedColor = props.isShowSelectedColor ?? true;
  const theme = useTheme();
  const { isToapplyV2Theme } = useApplyV2Theme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStudyUrlValid, setIsStudyUrlValid] = useState(true);
  const studyName = props.siteInfo.studyName || '';
  const nctId = props.siteInfo.nctid || '';
  const phoneNumber = props.siteInfo.contactPhoneNumber || '';
  const sitePhoneNumber = props.sitePhoneNumber || '';
  const siteDistance = props.siteInfo.distance ?? '';
  const isHybrid = props.siteInfo.isHybridSite || false;
  const isCompactCard = props?.isCompactCard || false;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const distanceMetricText = siteSettings?.distanceMetric?.fields?.label?.value || 'mi';
  const baseURL = siteSettings?.baseUrl?.value || '';

  // Check if study URL exists (not 404)
  useEffect(() => {
    const studyUrl = `${baseURL}find-trials/Study/${nctId}`;
    if (nctId && isValidUrl(studyUrl)) {
      checkUrlExists(studyUrl).then(setIsStudyUrlValid);
    } else {
      setIsStudyUrlValid(false);
    }
  }, [nctId, baseURL]);

  const selectedPlace = useSelector((state: RootState) => state.selectedSite);
  const dispatch = useDispatch();

  //TODO: this needs to compare with id, once we get from backend to highlight card
  const isSelected = selectedPlace?.siteInfo?.siteCode === props?.siteInfo?.siteCode || false;
  const buttonText = props.buttonText;
  const siteAddressFormat = props.siteAddressFormat;
  const borderColor = isSelected ? theme.palette.primary.main : theme.palette.neutral4.main;
  const modalButtonText = props.modalButtonText;
  const siteName = props?.siteInfo?.siteName || '';
  const hasNctIdRichTag = siteAddressFormat?.value?.includes('[nctId]');
  const hasPNRichTag = sitePhoneNumber?.value?.includes('[pn]');
  const hasSitePhoneNumberRichTag = sitePhoneNumber?.value?.includes('[sitePhoneNumber]');
  const isPhoneNumberRichTagEmpty = sitePhoneNumber?.value === '';
  const showPhoneNumber = hasPNRichTag
    ? props.callCenterDefaultNumber
    : hasSitePhoneNumberRichTag || isPhoneNumberRichTagEmpty
    ? phoneNumber
    : false;

  const onSiteSelect = (isConfirm = false) => {
    dispatch(
      siteSelection({
        siteInfo: props?.siteInfo as any,
        selectionType: isConfirm ? 'Confirm' : 'Partial',
        shouldInfoWindowVisible: true,
      })
    );
  };

  return (
    <>
      <Card
        tabIndex={0}
        sx={{
          borderRadius: '8px',
          border: isShowSelectedColor ? `2px solid ${borderColor}` : '',
          boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
        }}
        onClick={() => onSiteSelect()}
        onKeyDown={(event: { key: string }) => {
          if (event.key === 'Enter' || event.key === ' ') {
            onSiteSelect();
          }
        }}
        id="siteInfoCard"
        className="ui-site-info-card"
      >
        <CardContent sx={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <StCardTitleText gutterBottom data-testid="siteTitle">
              {props?.siteNameFormat?.value ? (
                <SiteNameRichText
                  style={{
                    margin: 0,
                    fontFamily:
                      siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
                      'Proxima Nova',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: theme.palette.black.main,
                  }}
                  siteNameFormat={props?.siteNameFormat}
                  siteInfo={props?.siteInfo}
                />
              ) : (
                siteName
              )}
            </StCardTitleText>
            {props?.siteInfo?.tier === 1 && props?.siteInfo?.isSitePremiere && (
              <Image field={props?.premiereSiteIcon} />
            )}
          </div>
          <Divider />
          {hasNctIdRichTag && (
            <Box sx={{ mb: 1, mt: 1 }}>
              <StudyNameHeadingWrapper
                clickable={!!nctId && isStudyUrlValid && hasNctIdRichTag}
                onClick={
                  nctId && hasNctIdRichTag && isStudyUrlValid
                    ? (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }
                    : undefined
                }
                data-testid="studyNameLink"
              >
                <UiHeading isChildren={true} size="h5">
                  {studyName}
                  {nctId && isStudyUrlValid && hasNctIdRichTag && (
                    <TextLinkIcon
                      fill={theme.palette.primary.main}
                      style={{
                        height: 24,
                        width: 24,
                        display: 'inline-block',
                        verticalAlign: 'baseline',
                        marginBottom: '-10px',
                        marginLeft: '4px',
                      }}
                    />
                  )}
                </UiHeading>
              </StudyNameHeadingWrapper>
            </Box>
          )}
          <Box
            sx={{
              '& p': {
                marginBlock: (theme: { spacing: (arg0: number) => any }) => theme.spacing(1),
                '& strong': {
                  fontWeight: 400,
                },
              },
            }}
          >
            <SiteLocationRichText
              style={{
                margin: 0,
                fontFamily:
                  siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value || 'Proxima Nova',
                fontSize: '14px',
                fontWeight: 400,
                color: theme.palette.black.main,
                minHeight: '35px',
              }}
              siteAddressFormat={siteAddressFormat}
              siteInfo={props.siteInfo}
            />
          </Box>
          {!isCompactCard && (
            <Box display="flex" columnGap="2rem" data-testid="phone-and-distance">
              {showPhoneNumber && (
                <Box display="flex" columnGap="5px" sx={{ alignItems: 'center' }}>
                  {/* <StNextImage field={props.phoneIcon} height="17px" width="17px" /> */}
                  <PhoneIcon
                    sx={{
                      color: (theme) => theme.palette.themeColor.main,
                      height: '17px',
                      width: '17px',
                    }}
                  />
                  <StCardText>{showPhoneNumber}</StCardText>
                </Box>
              )}
              {(siteDistance === null || siteDistance === '' || siteDistance === undefined) &&
              isHybrid ? (
                <Box display="flex" columnGap="5px" sx={{ alignItems: 'center' }}>
                  <Image field={props?.virtualIcon} />
                  <StCardText>{props.virutalSiteText?.value}</StCardText>
                </Box>
              ) : (
                <Box display="flex" columnGap="5px" sx={{ alignItems: 'center' }}>
                  {/* <StNextImage field={props.mapIcon} height="17px" width="17px" /> */}
                  <PlaceIcon
                    sx={{
                      color: (theme: { palette: { themeColor: { main: any } } }) =>
                        theme.palette.themeColor.main,
                      height: '17px',
                      width: '17px',
                      //alignSelf: 'baseline',
                    }}
                  />
                  <StCardText>
                    {siteDistance} {distanceMetricText}
                  </StCardText>
                </Box>
              )}
            </Box>
          )}
          {isToapplyV2Theme ? (
            <UiBox styles={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
              <ButtonPrimary
                className="btn"
                sx={{ minWidth: '200px' }}
                aria-label={props.cardButtonAriaLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onSiteSelect(true);
                }}
              >
                {typeof buttonText.value === 'number' || buttonText.value === undefined
                  ? String(buttonText.value || '')
                  : buttonText.value}
              </ButtonPrimary>
            </UiBox>
          ) : (
            <UIButton
              className="btn"
              aria-label={props.cardButtonAriaLabel}
              tabIndex={0}
              variant={isSelected ? 'contained' : 'outlined'}
              sx={{
                mt: '1rem',
                p: '4px',
                width: isCompactCard ? '20%' : '100%',
                minWidth: isCompactCard ? '120px' : '90%',
                left: isCompactCard ? '50%' : '0',
                transform: isCompactCard ? 'translateX(-50%)' : 'none',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSiteSelect(true);
              }}
            >
              <Text field={buttonText}></Text>
            </UIButton>
          )}
        </CardContent>
      </Card>
      {hasNctIdRichTag && isStudyUrlValid && (
        <SiteInfoModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          width="95vw"
          maxWidth="95vw"
        >
          <Box
            sx={{
              height: '60vh',
              mb: 2,
              width: '100%',
            }}
          >
            <StIframe
              id="study-details-iframe"
              title="Study Details"
              src={`${baseURL}find-trials/Study/${nctId}?loadType=iframe`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          </Box>
          <UiBox
            styles={{
              marginTop: '1rem',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <ButtonPrimary
              className="btn continue-site-info-card-btn"
              aria-label={props.cardButtonAriaLabel}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(false);
              }}
            >
              {typeof modalButtonText.value === 'number' || modalButtonText.value === undefined
                ? String(modalButtonText.value || '')
                : modalButtonText.value}
            </ButtonPrimary>
          </UiBox>
        </SiteInfoModal>
      )}
    </>
  );
};

export default SiteInfoCard;
